import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertTriangle } from 'lucide-react';

/**
 * Reusable bottom-sheet confirmation. Replaces AlertDialog & window.confirm across the app.
 *
 * <ConfirmSheet
 *    open={open}
 *    onOpenChange={setOpen}
 *    title="Delete this post?"
 *    description="This action cannot be undone."
 *    confirmLabel="Delete"
 *    cancelLabel="Cancel"
 *    destructive
 *    icon={<Trash className="w-5 h-5" />}
 *    onConfirm={async () => { ... }}
 *    loading={loading}
 * />
 */
export default function ConfirmSheet({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  loading = false,
  icon,
  testId,
}) {
  const handleConfirm = async () => {
    if (loading) return;
    if (onConfirm) await onConfirm();
    onOpenChange?.(false);
  };

  const iconWrap = destructive
    ? 'bg-[#FEE2E2] text-[#DC2626] border border-[#DC2626]/25'
    : 'bg-[#EFF2F7] text-[#0B4F3F] border border-[#E4E8EF]';

  const confirmBtn = destructive
    ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white'
    : 'bg-[#0B4F3F] hover:bg-[#083D31] text-white';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid={testId || 'confirm-sheet'}
        className="p-0 rounded-t-3xl border-[#E4E8EF]"
      >
        {/* Grabber handle */}
        <div className="flex justify-center pt-3 pb-1" aria-hidden>
          <div className="w-10 h-1 rounded-full bg-[#E4E8EF]" />
        </div>

        <div className="flex flex-col items-center text-center px-6 pt-4 pb-5">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${iconWrap}`}>
            {icon || <AlertTriangle className="w-6 h-6" strokeWidth={2.2} />}
          </div>
          <SheetHeader className="space-y-2">
            <SheetTitle className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight text-center">
              {title}
            </SheetTitle>
            {description && (
              <SheetDescription className="text-[14px] text-[#5B6675] leading-relaxed text-center">
                {description}
              </SheetDescription>
            )}
          </SheetHeader>
        </div>

        <div className="px-6 pb-8 pt-2 space-y-2.5 border-t border-[#E4E8EF] bg-[#F6F8FB]">
          <button
            data-testid={`${testId || 'confirm'}-confirm-btn`}
            onClick={handleConfirm}
            disabled={loading}
            className={`w-full h-12 rounded-full text-sm font-semibold transition-all disabled:opacity-60 active:scale-[0.98] shadow-[0_8px_20px_-8px_rgba(11,79,63,0.4)] ${confirmBtn}`}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
          <button
            data-testid={`${testId || 'confirm'}-cancel-btn`}
            onClick={() => onOpenChange?.(false)}
            disabled={loading}
            className="w-full h-12 rounded-full text-sm font-semibold text-[#0B1620] hover:bg-[#EFF2F7] transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
